import { get, isEmpty, isNil, merge } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import { GTM15_ICONS_CONFIG } from '@/components/MiningAndPumping/Toolbar/GtmDisplaySettingsModal/gtm15-icons/config';
import { getDataFromLocalStorage, putDataIntoLocalStorage } from '@/lib/localStorage';

import { ON_THE_WHOLE_STRATUM, WITHOUT_STRATUM } from './additional-stratums';

const GTM_SETTINGS_PDB_KEY = 'production-injection/gtm-settings';

const SETTINGS_TITLES = {
    gtm: 'ГТМ',
    gtm15: 'ГТМ 15',
    status: 'Состояния / простои',
    gdis: 'Исследования',
    drillingLiquidationDates: 'Бурение / Ликвидация',
    src: 'Источники данных',
};

const WELL_FINAL_STATE_NOT_HIGHLIGHTED = [
    'В работе',
    'Внутрициклический простой',
    'Техническая остановка',
    'Нет данных в источнике',
];

const mergeSettingsRecursive = (base, object) => {
    const array = (base ?? []).map(item => {
        const { key } = item;
        let updatedItem = item;
        if (object?.[key]) {
            updatedItem = { ...object[key] };
            if (updatedItem.children) {
                updatedItem.children = mergeSettingsRecursive(item.children, updatedItem.children);
            }
        }

        return updatedItem;
    });

    return array;
};

const normalizeGtmSettingsRequestData = settings => {
    if (isEmpty(settings)) return {};
    const normalizedGtmSettings = Object.entries(settings).reduce((accGtmSettings, [settingKey, settingData]) => {
        const selectedRows = [];
        const addSelectedRecord = record => selectedRows.push(record);
        const nodes = normalizeDataStructure(settingData, addSelectedRecord);

        accGtmSettings[settingKey] = { key: settingKey, title: SETTINGS_TITLES[settingKey], data: nodes, selectedRows };
        return accGtmSettings;
    }, {});
    return normalizedGtmSettings;
};

const normalizeDataStructure = (nodes, addSelectedRecord) => {
    if (isEmpty(nodes) || isNil(nodes)) return {};
    return nodes.reduce((accNodes, node) => {
        const { key, isVisible, children } = node;
        if (isVisible && isNil(children)) addSelectedRecord(node);
        accNodes[key] = { ...node };

        if (!isEmpty(children)) {
            const normalizedNodes = normalizeDataStructure(children, addSelectedRecord);
            if (!isEmpty(normalizedNodes)) accNodes[key].children = normalizedNodes;
        }
        return accNodes;
    }, {});
};

const normalizeDrillingData = data => {
    const prevColors = localStorage.getItem('drillingLiquidationColors')
        ? JSON.parse(localStorage.getItem('drillingLiquidationColors'))
        : undefined;
    return {
        drillingLiquidationDates: {
            key: data.drillingLiquidationDates.key,
            title: data.drillingLiquidationDates.title,
            selectedRows: [
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_1',
                    title: 'Заложение скважины',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_2',
                    title: 'Начало бурения',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_3',
                    title: 'Окончание бурения',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_4',
                    title: 'Принятие НГДУ',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_5',
                    title: 'Начало эксплуатации',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_6',
                    title: 'Ликвидация скважины',
                },
                {
                    dataSource: 3,
                    isVisible: true,
                    key: 'drillingLiquidationDates_7',
                    title: 'Ликвидация ствола',
                },
            ],

            data: {
                drillingLiquidationDates_1: {
                    key: 'drillingLiquidationDates_1',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Заложение скважины'}</span>,
                    titleValue: 'Заложение скважины',
                    value: 'LayngDate',
                    colorValue: prevColors?.drillingLiquidationDates_1 ?? '#46ED18',
                    data: data.drillingLiquidationDates?.data?.LayngDate ?? null,
                },
                drillingLiquidationDates_2: {
                    key: 'drillingLiquidationDates_2',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Начало бурения'}</span>,
                    titleValue: 'Начало бурения',
                    value: 'StartDate',
                    colorValue: prevColors?.drillingLiquidationDates_2 ?? '#1874ED',
                    data: data.drillingLiquidationDates?.data?.StartDate ?? null,
                },
                drillingLiquidationDates_3: {
                    key: 'drillingLiquidationDates_3',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Окончание бурения'}</span>,
                    titleValue: 'Окончание бурения',
                    value: 'EndDate',
                    colorValue: prevColors?.drillingLiquidationDates_3 ?? '#E2ED18',
                    data: data.drillingLiquidationDates?.data?.EndDate ?? null,
                },
                drillingLiquidationDates_4: {
                    key: 'drillingLiquidationDates_4',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Принятие НГДУ'}</span>,
                    value: 'AcceptDate',
                    titleValue: 'Принятие НГДУ',
                    colorValue: prevColors?.drillingLiquidationDates_4 ?? '#901090',
                    data: data.drillingLiquidationDates?.data?.AcceptDate ?? null,
                },
                drillingLiquidationDates_5: {
                    key: 'drillingLiquidationDates_5',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Начало эксплуатации'}</span>,
                    value: 'ExplStartDate',
                    titleValue: 'Начало эксплуатации',
                    colorValue: prevColors?.drillingLiquidationDates_5 ?? '#985E00',
                    data: data.drillingLiquidationDates?.data?.ExplStartDate ?? null,
                },
                drillingLiquidationDates_6: {
                    key: 'drillingLiquidationDates_6',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Ликвидация скважины'}</span>,
                    value: 'LiqWellDate',
                    titleValue: 'Ликвидация скважины',
                    colorValue: prevColors?.drillingLiquidationDates_6 ?? '#FD0808',
                    data: data.drillingLiquidationDates?.data?.LiqWellDate ?? null,
                },
                drillingLiquidationDates_7: {
                    key: 'drillingLiquidationDates_7',
                    title: <span style={{ display: 'inline-block', width: '150px' }}>{'Ликвидация ствола'}</span>,
                    value: 'LiqStvolDate',
                    titleValue: 'Ликвидация ствола',
                    colorValue: prevColors?.drillingLiquidationDates_7 ?? '#FF9500',
                    data: data.drillingLiquidationDates?.data?.LiqStvolDate ?? null,
                },
            },
        },
    };
};

const gtm = {
    name: 'gtm',
    state: {
        selectedSettingKey: 'gtm',
        settings: null,
        data: null,
        rawParameters: null,
    },
    reducers: {
        setSelectedSettingKey(state, payload) {
            return { ...state, selectedSettingKey: payload };
        },
        setSettings(state, payload) {
            return { ...state, settings: payload };
        },
        setData(state, payload) {
            return { ...state, data: payload };
        },
        setRawParameters(state, payload) {
            if (payload === state.rawParameters) return state;
            return { ...state, rawParameters: payload };
        },
    },

    effects: () => ({
        async setSettings(payload) {
            if (payload) {
                const settingsPDB = getDataFromLocalStorage({ key: GTM_SETTINGS_PDB_KEY });
                const settings = settingsPDB?.data;
                const updatedPayload = Object.keys(payload ?? {}).reduce((accGtmChart, gtmType) => {
                    const { data } = payload[gtmType] ?? {};
                    let updatedData = JSON.parse(JSON.stringify(data));
                    if (settings?.[gtmType]) {
                        updatedData = merge(settings[gtmType], updatedData);
                    }
                    accGtmChart[gtmType] = updatedData;
                    return accGtmChart;
                }, {});

                putDataIntoLocalStorage({ key: GTM_SETTINGS_PDB_KEY, data: updatedPayload });
            }
        },

        async prepareGtmSettingsRequestData({ gtmChart, additionalInfo }) {
            const settingsPDB = getDataFromLocalStorage({ key: GTM_SETTINGS_PDB_KEY });
            const settings = settingsPDB?.data;
            let updatedGtmChart = gtmChart;
            if (settings && updatedGtmChart) {
                updatedGtmChart = Object.keys(gtmChart).reduce((accGtmChart, type) => {
                    let settingsByType = gtmChart[type];
                    if (settings[type]) {
                        settingsByType = mergeSettingsRecursive(gtmChart[type], settings[type]);
                    }

                    accGtmChart[type] = settingsByType;
                    return accGtmChart;
                }, {});
            }

            const normalizedData = normalizeGtmSettingsRequestData(updatedGtmChart);
            this.setSettings({ ...normalizedData, ...additionalInfo[0], ...normalizeDrillingData(additionalInfo[1]) });
        },
    }),

    selectors: (slice, createSelector) => ({
        selectedRowsFiltered() {
            return createSelector(
                rootState => rootState.gtm.settings,
                settings => {
                    if (settings) {
                        const selectedRowsFiltered = {};
                        Object.entries(settings).forEach(
                            ([settingKey, { title: settingTitle, selectedRows, data: settingData }]) => {
                                if (selectedRows) {
                                    selectedRows.forEach(row => {
                                        const { key, title, type, path = [], children, dataSource } = row;
                                        let extendedRow = {};

                                        // FIXME: необходимо разобраться(переделать) с генерацией сеттингов для графика

                                        if (!type && settingKey === 'gdis') {
                                            const [mainParentKey = key] = path;
                                            const { color } = settingData[mainParentKey];
                                            extendedRow = {
                                                key,
                                                color,
                                                type: settingKey,
                                                ...settings[settingKey]?.data[key],
                                            };
                                            selectedRowsFiltered[key] = extendedRow;
                                        }

                                        if (!type) {
                                            const [mainParentKey = key] = path;
                                            if (settingKey === 'gtm' && settings.src?.data?.[dataSource]?.isVisible) {
                                                const {
                                                    color,
                                                    signature,
                                                    title: mainParentTitle,
                                                } = settingData[mainParentKey];
                                                const parentPath = path.reduce((accPath, pathKey, ix) => {
                                                    if (!ix) accPath.push(pathKey);
                                                    else accPath.push('children', pathKey);
                                                    return accPath;
                                                }, []);
                                                const { title: parentTitle } = get(settingData, parentPath);

                                                const group = {
                                                    key: mainParentKey,
                                                    title: signature || mainParentTitle || '',
                                                };

                                                extendedRow = {
                                                    key,
                                                    group,
                                                    title: parentTitle,
                                                    srcTitle: settings.src?.data?.[dataSource]?.title || '',
                                                    type: settingKey,
                                                    typeTitle: settingTitle,
                                                    color,
                                                };
                                            } else if (settingKey === 'gtm15') {
                                                const { title: mainParentTitle, iconType } = settingData[mainParentKey];
                                                extendedRow = {
                                                    key,
                                                    title: mainParentTitle,
                                                    type: settingKey,
                                                    typeTitle: settingTitle,
                                                    icon: GTM15_ICONS_CONFIG[iconType]?.icon?.svg ?? null,
                                                };
                                            } else if (settingKey === 'status') {
                                                const { color } = settingData[mainParentKey];
                                                extendedRow = {
                                                    key,
                                                    type: settingKey,
                                                    typeTitle: settingTitle,
                                                    color,
                                                    title,
                                                };
                                            } else if (settingKey === 'drillingLiquidationDates') {
                                                const {
                                                    title: mainParentTitle,
                                                    colorValue,
                                                    titleValue,
                                                    data,
                                                } = settingData[mainParentKey];
                                                if (!data) return;
                                                extendedRow = {
                                                    key,
                                                    colorValue,
                                                    color: colorValue,
                                                    date: data,
                                                    signature: titleValue,
                                                    title: mainParentTitle,
                                                };
                                            }
                                            if (isEmpty(extendedRow)) return;

                                            selectedRowsFiltered[key] = extendedRow;
                                        }
                                    });
                                }
                            },
                        );
                        return selectedRowsFiltered;
                    }
                    return null;
                },
            );
        },
        drillingLiquidationDates(models) {
            return createSelector(models.gtm.selectedRowsFiltered, selectedRows => {
                const filteredData = [];

                if (selectedRows?.drillingLiquidationDates_1) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_1',
                        date: selectedRows?.drillingLiquidationDates_1.date,
                        signature: selectedRows?.drillingLiquidationDates_1.signature,
                        color: selectedRows?.drillingLiquidationDates_1.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_2) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_2',
                        date: selectedRows?.drillingLiquidationDates_2.date,

                        signature: selectedRows?.drillingLiquidationDates_2.signature,
                        color: selectedRows?.drillingLiquidationDates_2.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_3) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_3',
                        date: selectedRows?.drillingLiquidationDates_3.date,

                        signature: selectedRows?.drillingLiquidationDates_3.signature,
                        color: selectedRows?.drillingLiquidationDates_3.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_4) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_4',
                        date: selectedRows?.drillingLiquidationDates_4.date,

                        signature: selectedRows?.drillingLiquidationDates_4.signature,
                        color: selectedRows?.drillingLiquidationDates_4.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_5) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_5',
                        date: selectedRows?.drillingLiquidationDates_5.date,

                        signature: selectedRows?.drillingLiquidationDates_5.signature,
                        color: selectedRows?.drillingLiquidationDates_5.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_6) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_6',
                        date: selectedRows?.drillingLiquidationDates_6.date,

                        signature: selectedRows?.drillingLiquidationDates_6.signature,
                        color: selectedRows?.drillingLiquidationDates_6.color,
                    });
                }
                if (selectedRows?.drillingLiquidationDates_7) {
                    filteredData.push({
                        key: 'drillingLiquidationDates_7',
                        date: selectedRows?.drillingLiquidationDates_7.date,

                        signature: selectedRows?.drillingLiquidationDates_7.signature,
                        color: selectedRows?.drillingLiquidationDates_7.color,
                    });
                }
                for (let i = 1; i < filteredData.length; ++i) {
                    if (filteredData[i].date === filteredData[i - 1].date) {
                        filteredData[i].signature = [filteredData[i].signature, filteredData[i - 1].signature].flat();
                        filteredData[i - 1].signature = '';
                    }
                }
                return filteredData;
            });
        },

        filteredData(models) {
            return createSelector(
                rootState => rootState.gtm.data,
                models.gtm.selectedRowsFiltered,
                rootState => rootState.chartsWidget.stratums,
                rootState => rootState.chartsWidget.activeStratums,
                rootState => rootState.miningAndPumping.wellFinalState,
                rootState => rootState.chartsWidget.isStratumsTimelineVisible,
                rootState => rootState.gdis.data,
                (data, selectedRows, stratums, activeStratums, wellFinalState, isStratumsTimelineVisible, gdis) => {
                    if (!isEmpty(stratums)) {
                        let filteredData = [];

                        const generateData = gdisData => {
                            const result = [];
                            if (!gdisData) return [];
                            Object.values(gdisData).forEach((gdis, idxParent) => {
                                Object.entries(gdis).forEach(([key, item], idx) => {
                                    const date = dayjs(item.date).format('DD.MM.YYYY');
                                    result.push({
                                        key,
                                        entityKey: `gdis_${idxParent}_${idx}`,
                                        type: key,
                                        date: date,
                                        parameters: null,
                                        params: item.params,
                                        points: [dayjs(item.date).format('MM.YYYY')],
                                        color: '#FC8E28',
                                        comment: '',
                                        group: {
                                            key: `gdis_${idxParent}_${idx}`,
                                            title: `${item.stratumTitle} ${item.shortName}`,
                                        },
                                        title: `${item.stratumTitle} ${item.shortName}`,
                                    });
                                });
                            });
                            return result;
                        };
                        const processedGdis = generateData(gdis);
                        if (data && !isEmpty(selectedRows)) {
                            filteredData = [...data, ...processedGdis].reduce(
                                (accData, { type, dates, comment, parameters, stratumId }, _, currArray) => {
                                    const metaGtm = selectedRows[type];

                                    if (metaGtm && activeStratums[stratumId]) {
                                        dates.forEach(date => {
                                            const [_, month, year] = date?.split('.');
                                            const points = [`${month}.${year}`];

                                            const stratumTitle =
                                                stratums[stratumId]?.title ??
                                                (ON_THE_WHOLE_STRATUM.id === stratumId
                                                    ? ON_THE_WHOLE_STRATUM.title
                                                    : null);

                                            const result = {
                                                key: `${date}_${type}`,
                                                entityKey: type,
                                                title: metaGtm?.title,
                                                type: metaGtm.type,
                                                typeTitle: metaGtm.typeTitle,
                                                parameters,
                                                date: null,
                                                stratumTitle,
                                                srcTitle: metaGtm.srcTitle,
                                                comment,
                                                points,
                                            };
                                            if (metaGtm.group) result.group = metaGtm.group;
                                            if (metaGtm.color) result.color = metaGtm.color;
                                            if (metaGtm.icon) result.icon = metaGtm.icon;
                                            accData.push(result);
                                        });
                                    }
                                    if (metaGtm && metaGtm.type === 'gdis') {
                                        const result = {
                                            key: metaGtm.key,
                                            entityKey: type,
                                            title: metaGtm?.title,
                                            type: metaGtm.type,
                                            typeTitle: metaGtm.typeTitle,
                                            parameters,
                                            params: metaGtm.params,
                                            date: null,
                                            srcTitle: metaGtm.srcTitle,
                                            comment,
                                            points: metaGtm.points,
                                            icon: (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M8.43301 10.25C8.24056 10.5833 7.75944 10.5833 7.56699 10.25L4.96891 5.75C4.77646 5.41667 5.01702 5 5.40192 5L10.5981 5C10.983 5 11.2235 5.41667 11.0311 5.75L8.43301 10.25Z"
                                                        fill="#FC8E28"
                                                    />
                                                </svg>
                                            ),
                                            ...metaGtm,
                                        };
                                        accData.push(result);
                                    }
                                    return accData;
                                },
                                [],
                            );

                            const { date, state: title } = wellFinalState || {};
                            const filteredSelectedRows = Object.values(selectedRows).filter(
                                row => row.type === 'status' && row.title === title,
                            );
                            const stratumsIds = Object.keys(stratums);

                            if (
                                date &&
                                filteredSelectedRows?.length > 0 &&
                                stratumsIds.some(id => activeStratums[id])
                            ) {
                                // eslint-disable-next-line no-unused-vars
                                const [_, month, year] = date?.split('.');
                                const points = [`${month}.${year}`];
                                const wellFinalStateObj = {
                                    key: 'wellFinalState',
                                    points,
                                    title,
                                    date,
                                    color: '#FF5252',
                                };
                                if (WELL_FINAL_STATE_NOT_HIGHLIGHTED.some(state => state === title)) {
                                    wellFinalStateObj.type = 'status';
                                } else {
                                    wellFinalStateObj.type = 'wellFinalState';
                                }
                                filteredData.push(wellFinalStateObj);
                            }
                        }

                        if (isStratumsTimelineVisible) {
                            const format = 'MM.YYYY';
                            Object.values(stratums)
                                .sort((a, b) => {
                                    if (a.id === ON_THE_WHOLE_STRATUM.id) return -1;
                                    const [firstDateA] = a.timeline;
                                    const [firstDateB] = b.timeline;
                                    const momentDateA = dayjs(firstDateA, format);
                                    const momentDateB = dayjs(firstDateB, format);
                                    return momentDateA.isBefore(momentDateB) ? -1 : 1;
                                })
                                .forEach(({ id, timeline, title }) => {
                                    if (timeline && id !== WITHOUT_STRATUM.id) {
                                        filteredData.push({
                                            key: `stratum${id}`,
                                            title,
                                            type: 'timeline',
                                            points: timeline.flat(),
                                        });
                                    }
                                });
                        }
                        return [...filteredData];
                    }
                    return null;
                },
            );
        },
        parameters() {
            return createSelector(
                rootState => rootState.gtm.rawParameters,
                rawParameters => {
                    let updatedParameters = null;
                    if (rawParameters) {
                        updatedParameters = rawParameters.reduce((accParameters, parameter) => {
                            const [type, data] = parameter;
                            if (type !== 'timeline' && type !== 'wellFinalState') {
                                const children = (data ?? []).reduce(
                                    (accChildren, { key, title, date, parameters }) => {
                                        if (!isEmpty(parameters)) {
                                            accChildren.push({
                                                key,
                                                title: `${title} (${date})`,
                                                children: parameters,
                                            });
                                        }
                                        return accChildren;
                                    },
                                    [],
                                );
                                if (!isEmpty(children)) {
                                    const gtmRow = {
                                        key: type,
                                        title: SETTINGS_TITLES[type],
                                        children,
                                    };
                                    accParameters.push(gtmRow);
                                }
                            }
                            return accParameters;
                        }, []);
                    }
                    return updatedParameters;
                },
            );
        },
    }),
};
export default gtm;
